import React from "react"
import ReactDOM from "react-dom"

import { Button } from "../button/Button"
import { HotKeySet } from "../keyboard/HotKeyResponder"
import { Text } from "../text/Text"
import { RawFrameSource } from "./PollingSession"

export interface CameraPopupProps {
  /** Callback when user confirms the captured image. Receives the raw data URL. */
  onConfirm: (dataUrl: string) => void
  /** Callback when user cancels capture. */
  onCancel: () => void
  /**
   * Callback when camera acquisition fails. The popup shows its own error state,
   * so a caller with nothing better to offer can leave it open; one that treats
   * a camera as mandatory closes it instead.
   */
  onError: () => void
  /**
   * Requested capture width in pixels, passed to getUserMedia as an `ideal`
   * constraint. The browser returns the closest resolution the camera supports
   * and degrades gracefully when it cannot match. Without this, browsers default
   * to 640x480 regardless of the camera's true capability.
   */
  requestedWidth?: number
  /**
   * Requested capture height in pixels, passed to getUserMedia as an `ideal`
   * constraint. See `requestedWidth` for behavior.
   */
  requestedHeight?: number
  /**
   * Run as a polling viewfinder: stay in the `viewfinder` phase for the whole
   * session and let a caller pull frames on demand.
   *
   * Suppresses the Capture control, the `preview` phase, and the Confirm/Retake
   * controls, because a Capture gesture is meaningless while a loop is already
   * submitting frames and Confirm/Retake are unreachable without a preview.
   * Everything about the presentation is gated on this flag, so the popup
   * renders exactly as it did before the feature when it is false or absent.
   */
  pollingMode?: boolean
  /**
   * Invoked with a frame source once the polling viewfinder is live, and with
   * null when it tears down.
   *
   * This popup is the sole holder of the `<video>` element and the
   * `MediaStream`, so handing a puller upward is the only way anything else can
   * reach a live frame. Only invoked with a source when `pollingMode` is true.
   */
  onFrameSourceReady?: (source: RawFrameSource | null) => void
  /**
   * Invoked when the worker asks to key the date in by hand rather than wait
   * for the loop to read it.
   *
   * Only offered in polling mode, and only rendered at all when supplied: a
   * consumer with no manual path would otherwise show a control that leads
   * nowhere. Distinct from `onCancel` because the two decisions differ — see
   * `handleRequestManualEntry`.
   */
  onRequestManualEntry?: () => void
  /**
   * Hot keys for the popup controls. Flat rather than an object because each one
   * pairs with the callback above it, and a control with no callback has no key.
   * ImageCapture resolves these from its `hotKeys` prop.
   */
  captureHotKey?: HotKeySet
  confirmHotKey?: HotKeySet
  retakeHotKey?: HotKeySet
  cancelHotKey?: HotKeySet
  manualEntryHotKey?: HotKeySet
}

export type CameraPopupPhase = "viewfinder" | "preview" | "error"

export interface CameraPopupState {
  /** Current phase of the popup UI. */
  phase: CameraPopupPhase
  /** Active media stream for the viewfinder. */
  mediaStream: MediaStream | null
  /** Data URL of the captured frame (set when phase is "preview"). */
  capturedDataUrl: string | null
}

/**
 * Fullscreen camera popup overlay for WebRTC capture.
 *
 * Provides a Capture → Preview → Confirm/Retake flow.
 * Renders as a portal into document.body to sit above all page content.
 *
 * This is an internal component — not exported from the public API.
 */
export class CameraPopup extends React.PureComponent<CameraPopupProps, CameraPopupState> {
  private readonly videoRef = React.createRef<HTMLVideoElement>()
  private mounted = false

  public constructor(props: CameraPopupProps) {
    super(props)
    this.state = {
      phase: "viewfinder",
      mediaStream: null,
      capturedDataUrl: null,
    }
  }

  public componentDidMount(): void {
    this.mounted = true
    this.acquireStream()
  }

  public componentWillUnmount(): void {
    this.mounted = false
    this.props.onFrameSourceReady?.(null)
    this.stopStream()
  }

  /**
   * Request camera via getUserMedia with facingMode: "environment" preference.
   * Requests the configured resolution as an `ideal` constraint so cameras
   * stream at their higher native resolution instead of the browser's 640x480
   * default. Falls back to any camera ({ video: true }) if environment mode fails.
   * On complete failure, calls onError to notify the parent.
   */
  private async acquireStream(): Promise<void> {
    const { requestedWidth, requestedHeight } = this.props
    const videoConstraints: MediaTrackConstraints = { facingMode: "environment" }
    if (requestedWidth) {
      videoConstraints.width = { ideal: requestedWidth }
    }
    if (requestedHeight) {
      videoConstraints.height = { ideal: requestedHeight }
    }

    try {
      let stream: MediaStream
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: videoConstraints,
        })
      } catch (envErr) {
        console.warn("Rear camera unavailable, trying any camera:", envErr)
        // Drop only facingMode. The resolution request is unrelated to which
        // camera was picked, and a frame the OCR cannot read is no more useful
        // from the front camera than from the back.
        const { facingMode, ...withoutFacingMode } = videoConstraints
        stream = await navigator.mediaDevices.getUserMedia({
          video: withoutFacingMode,
        })
      }

      // Component may have unmounted while awaiting getUserMedia
      if (!this.mounted) {
        stream.getTracks().forEach((t) => t.stop())
        return
      }

      this.setState({ mediaStream: stream, phase: "viewfinder" }, () => {
        if (this.videoRef.current) {
          this.videoRef.current.srcObject = stream
        }
        if (this.props.pollingMode) {
          this.props.onFrameSourceReady?.({ grabFrame: this.drawCurrentFrame })
        }
      })
    } catch (err) {
      console.error("Failed to acquire camera stream:", err)
      if (this.mounted) {
        // Show the failure in place. A caller that requires a camera unmounts
        // this popup in onError, so this phase is only seen as a last resort.
        this.setState({ phase: "error", mediaStream: null })
        this.props.onError()
      }
    }
  }

  /**
   * Stop all tracks on the active MediaStream and clear the reference.
   */
  private stopStream(): void {
    if (this.state.mediaStream) {
      this.state.mediaStream.getTracks().forEach((track) => track.stop())
      if (this.mounted) {
        this.setState({ mediaStream: null })
      }
    }
  }

  /**
   * Draw the frame currently showing in the live `<video>` element to an
   * offscreen canvas and return it as a lossless PNG data URL.
   */
  private drawCurrentFrame = (): string | null => {
    const video = this.videoRef.current
    if (!video || video.videoWidth === 0 || video.videoHeight === 0) {
      return null
    }

    const canvas = document.createElement("canvas")
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext("2d")
    if (!ctx) {
      console.error("Failed to get canvas 2D context")
      return null
    }

    ctx.drawImage(video, 0, 0)
    // Capture the intermediate frame losslessly (PNG). ImageCapture re-encodes
    // it to the requested output format once, so a lossy JPEG here would just
    // bake in artifacts that the final encode compounds.
    return canvas.toDataURL("image/png")
  }

  private handleCapture = () => {
    const dataUrl = this.drawCurrentFrame()
    if (!dataUrl) {
      return
    }

    this.stopStream()
    this.setState({
      phase: "preview",
      capturedDataUrl: dataUrl,
    })
  }

  /**
   * Discard the captured frame and reacquire the camera stream
   * to return to the viewfinder phase.
   */
  private handleRetake = () => {
    this.setState({ capturedDataUrl: null })
    this.acquireStream()
  }

  /**
   * Retry after a failed acquisition: the common causes are transient, like
   * another application holding the device or a pending permission prompt.
   *
   * Returns to the viewfinder phase first so a second failure re-enters the
   * error state rather than appearing to do nothing.
   */
  private handleRetryCamera = () => {
    this.setState({ phase: "viewfinder" })
    this.acquireStream()
  }

  /**
   * Confirm the captured image by passing the data URL to the parent.
   */
  private handleConfirm = () => {
    if (this.state.capturedDataUrl) {
      this.props.onConfirm(this.state.capturedDataUrl)
    }
  }

  /**
   * Cancel capture: stop the stream and notify the parent.
   */
  private handleCancel = () => {
    this.props.onFrameSourceReady?.(null)
    this.stopStream()
    this.props.onCancel()
  }

  /**
   * The worker opts out of automatic reading and asks to type the date instead.
   */
  private handleRequestManualEntry = () => {
    this.props.onRequestManualEntry?.()
    this.props.onFrameSourceReady?.(null)
    this.stopStream()
  }

  public render() {
    const { phase, capturedDataUrl } = this.state
    const { pollingMode } = this.props
    const failed = phase === "error"
    // Under polling the phase never becomes "preview", so the video element is
    // always the content
    const showViewfinder = !failed && (pollingMode || phase === "viewfinder")

    const overlay = (
      <div className="camera-popup">
        <div className="camera-popup__content">
          {failed ? (
            // Keeps the viewfinder's footprint so the overlay does not resize
            // out from under the worker.
            <div className="camera-popup__error" role="alert">
              <Text>Could not initialize camera.</Text>
            </div>
          ) : showViewfinder ? (
            <video
              ref={this.videoRef}
              className="camera-popup__video"
              autoPlay={true}
              playsInline={true}
              muted={true}
            />
          ) : (
            <img
              className="camera-popup__preview"
              src={capturedDataUrl || undefined}
              alt="Captured preview"
            />
          )}
        </div>
        <div className="camera-popup__controls">
          {failed ? (
            <>
              <Button
                variant="primary"
                hotKey={this.props.retakeHotKey}
                onClick={this.handleRetryCamera}
              >
                <Text>Try again</Text>
              </Button>
              <Button
                variant="primary"
                hotKey={this.props.cancelHotKey}
                onClick={this.handleCancel}
              >
                <Text>Cancel</Text>
              </Button>
            </>
          ) : pollingMode ? (
            <>
              <Button
                variant="primary"
                hotKey={this.props.cancelHotKey}
                onClick={this.handleCancel}
              >
                <Text>Cancel</Text>
              </Button>
              {this.props.onRequestManualEntry && (
                <Button
                  variant="secondary"
                  hotKey={this.props.manualEntryHotKey}
                  onClick={this.handleRequestManualEntry}
                >
                  <Text>Enter manually</Text>
                </Button>
              )}
            </>
          ) : phase === "viewfinder" ? (
            <>
              <Button
                variant="primary"
                hotKey={this.props.captureHotKey}
                onClick={this.handleCapture}
              >
                <Text>Capture</Text>
              </Button>
              <Button
                variant="primary"
                hotKey={this.props.cancelHotKey}
                onClick={this.handleCancel}
              >
                <Text>Cancel</Text>
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="secondary"
                hotKey={this.props.confirmHotKey}
                onClick={this.handleConfirm}
              >
                <Text>Confirm</Text>
              </Button>
              <Button
                variant="secondary"
                hotKey={this.props.retakeHotKey}
                onClick={this.handleRetake}
              >
                <Text>Retake</Text>
              </Button>
            </>
          )}
        </div>
      </div>
    )

    return ReactDOM.createPortal(overlay, document.body)
  }
}
